import classNames from 'classnames'
import createDebug from 'debug'
import { debounce } from 'lodash'
import React, { memo, useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { UncontrolledTooltip } from 'reactstrap'
import { ReactComponent as ChevronLeft } from '../../../../assets/icons/chevron-left.svg'
import { ReactComponent as ChevronRight } from '../../../../assets/icons/chevron-right.svg'
import { usePreviewArtworks } from '../../../../hooks/usePreviewArtworks'
import { usePreviewCustomShape } from '../../../../hooks/usePreviewCustomShape'
import PubSub from '../../Broker'
import { useSelectedVariantContext } from "../../context/SelectedVariantContext"
import { actionChangeTabDesign, updateActiveSide } from "../../context/SelectedVariantContext/action"
import { destroyMockup2DGeneratorInstance, getMockup2DGeneratorInstance } from "./mockup-2d/Mockup2DGenerator"
import { destroyBabylonInstance, getBabylonInstance } from "./preview-3d/Babylon"
import cutDesignParts from "./preview-3d/cutDesignParts"

const debug = createDebug('pf126:PreviewLiveArtworkContainer')

const PreviewLiveArtworkContainer = memo(() => {
    const {
        state: { designs, selectedSide, selectedAttribute, loadingImageLayer, pickerAttributes, designMode },
        dispatch,
    } = useSelectedVariantContext()
    const {fetchPreview3D} = usePreviewArtworks()

    const {
        preview_v3: mockupPreviewV3,
        meta: mockupMeta,
    } = selectedAttribute?.mockup || {}

    const preview3DCanvasRef = useRef(null)
    const currentDesignModeRef = useRef(designMode)

    const {pixiCanvasRef, isCustomShape, isReady, isUpdating} = usePreviewCustomShape()

    const [previewMode, setPreviewMode] = useState('')
    const [selectedMockupView, setSelectedMockupView] = useState(0)
    const [mockupImages, setMockupImages] = useState([])

    const startPreview3D = () => {
        const babylon = getBabylonInstance(preview3DCanvasRef.current, mockupPreviewV3, (event, data) => {
            if (event === 'mesh_double_clicked') {
                const side = data?.split('_')?.[0]
                changeSide(side)
            }
        })
        babylon?.run()
    }

    const startMockup2D = () => {
        getMockup2DGeneratorInstance()
    }

    const load3DModel = async () => {
        const babylon = getBabylonInstance()
        babylon?.loadMaterialTexture(mockupPreviewV3.material_url, mockupPreviewV3.texture)
        await babylon?.loadModel(mockupPreviewV3.model_url, mockupPreviewV3.material_id)
        babylon?.updateDesignTexture()
    }

    const loadMockupInfos = () => {
        if (!selectedAttribute?.mockup?.meta?.use_client_mockup || !mockupMeta?.mockup_infos?.length) return
        const mockup2DGenerator = getMockup2DGeneratorInstance()
        mockup2DGenerator?.loadMockupInfos(mockupMeta.mockup_infos)
    }

    const updateUVMapping = async () => {
        const isDesignModeChanged = currentDesignModeRef.current !== designMode
        if (!isDesignModeChanged && (selectedSide === 'All' || selectedSide === 'Safezone')) return

        const artworksData = fetchPreview3D()
        if (!artworksData) return

        for (const artwork of artworksData) {
            if (!artwork?.canvas) continue

            const templateConfigs = mockupPreviewV3['template_configs'][artwork.side]
            if (!templateConfigs) return

            const designParts = await cutDesignParts(
                artwork?.canvas,
                templateConfigs,
                mockupPreviewV3.texture.scale,
            )

            const babylon = getBabylonInstance()
            designParts.forEach((designPart, i) => {
                babylon?.drawUV(designPart.image, artwork.side, i)
            })
        }
    }

    const updateMockup2D = async () => {
        const isDesignModeChanged = currentDesignModeRef.current !== designMode
        if (!isDesignModeChanged && (selectedSide === 'All' || selectedSide === 'Safezone')) return

        const startTs = Date.now()
        let artwork = fetchPreview3D(true, 1)
        if (!artwork) return

        debug(`[updateMockup2D] fetchPreview3D | ${Date.now() - startTs} ms`)

        const colorAttr = selectedAttribute?.attributes?.find((a) => a.type === 'color')

        const mockup2DGenerator = getMockup2DGeneratorInstance()
        const mockups = await mockup2DGenerator.render(artwork, colorAttr?.color_code || '#ffffff')

        // mockupImages.forEach(URL.revokeObjectURL)
        setMockupImages(mockups)

        const ctx = artwork.getContext('2d')
        ctx?.clearRect(0, 0, artwork.width, artwork.height)
        artwork = null
    }

    const switchPreview = (mode) => {
        if (mode === previewMode) return
        setPreviewMode(mode)
    }

    useEffect(() => {
        if (isCustomShape) {
            setPreviewMode('pixi')
        }
    }, [isCustomShape])

    const debounceUpdateUVMapping = debounce(() => {
        updateUVMapping()
    }, 50)

    const debounceUpdateMockup2D = debounce(() => {
        updateMockup2D()
    }, 200)

    const changeSide = (side) => {
        if (!side || !selectedAttribute.safeZone[side]) return
        // if (side === selectedSide) return
        dispatch(updateActiveSide(side))
        dispatch(actionChangeTabDesign(''))
    }

    useEffect(() => {
        if (isCustomShape) return

        if (mockupPreviewV3?.model_url) {
            startPreview3D()
            load3DModel()
            setPreviewMode('3d')
        }

        return () => {
            destroyBabylonInstance()
        }
    }, [mockupPreviewV3, isCustomShape])

    useEffect(() => {
        if (isCustomShape) return

        if (mockupMeta?.use_client_mockup) {
            startMockup2D()
            if (mockupMeta?.mockup_infos?.length) {
                loadMockupInfos()
            }
            setPreviewMode('mockup')
        }

        return () => {
            destroyMockup2DGeneratorInstance()
        }
    }, [mockupMeta, isCustomShape])

    useEffect(() => {
        if (isCustomShape) return

        const subscriber = PubSub.subscribe('UPDATE_3D_MODEL_TEXTURE', () => {
            if (selectedAttribute?.mockup?.preview_v3?.model_url) {
                debounceUpdateUVMapping()
            }
            if (selectedAttribute?.mockup?.meta?.use_client_mockup) {
                debounceUpdateMockup2D()
            }
        })

        return () => {
            PubSub.unsubscribe(subscriber)
        }
    }, [pickerAttributes, designs, selectedAttribute, isCustomShape])

    useEffect(() => {
        if (loadingImageLayer) return
        if (isCustomShape) return

        if (selectedAttribute?.mockup?.preview_v3?.model_url) {
            debounceUpdateUVMapping()
        }

        if (selectedAttribute?.mockup?.meta?.use_client_mockup) {
            debounceUpdateMockup2D()
        }

        return () => {
            debounceUpdateUVMapping.cancel()
            debounceUpdateMockup2D.cancel()
            currentDesignModeRef.current = designMode
        }
    }, [pickerAttributes, designs, selectedAttribute, loadingImageLayer, selectedAttribute?.safeZone[selectedSide]?.fill, designMode, isCustomShape])

    useEffect(() => {
        if (selectedSide === 'All' || selectedSide === 'Safezone') return
        const babylon = getBabylonInstance()
        babylon?.focusOnSide(selectedSide)
    }, [selectedSide])

    useEffect(() => {
        if (selectedMockupView >= mockupImages.length && mockupImages.length > 0) {
            setSelectedMockupView(0)
        }
    }, [mockupImages, selectedMockupView])

    return (
        <div id="preview_live_artwork_container" className="PreviewLiveArtworkContainer d-flex flex-column w-100  pt-3">
            <div className="d-flex justify-content-center">
                {!isCustomShape && (
                    <>
                        <button
                            type="button"
                            onClick={() => switchPreview('mockup')}
                            className={classNames('Preview2DButton', {active: previewMode === 'mockup'})}
                            disabled={!selectedAttribute?.mockup?.meta?.use_client_mockup}
                        >
                            Mockup
                        </button>
                        <button
                            type="button"
                            id="Preview3DButton"
                            onClick={() => switchPreview('3d')}
                            className={classNames('Preview3DButton', {active: previewMode === '3d'})}
                            disabled={!selectedAttribute?.mockup?.preview_v3?.model_url}
                        >
                            3D
                        </button>
                        {!selectedAttribute?.mockup?.preview_v3?.model_url && (
                            <UncontrolledTooltip placement="bottom" target="Preview3DButton">
                                Coming soon
                            </UncontrolledTooltip>
                        )}
                    </>
                )}
            </div>
            <div
                id="preview_live_artwork_canvas"
                className="PreviewLiveArtworkCanvas d-flex flex-column align-items-center justify-content-center"
            >
                {!!selectedAttribute?.mockup?.meta?.use_client_mockup && (
                    <div
                        className="flex-column w-100 h-100 justify-content-center"
                        style={{display: previewMode === 'mockup' ? 'flex' : 'none'}}
                    >
                        <div
                            className="d-flex flex-column justify-content-center align-items-center"
                            style={{flexGrow: 1}}
                        >
                            {mockupImages[selectedMockupView] ? (
                                <img
                                    src={mockupImages[selectedMockupView]}
                                    alt="Mockup"
                                    className="w-100"
                                    style={{aspectRatio: 1, maxWidth: '30vw'}}
                                />
                            ) : (
                                <div className="spinner-border"></div>
                            )}
                        </div>
                        <div className="d-flex justify-content-center">
                            <div style={{padding: '30px 12px', width: '292px'}}>
                                <Slider
                                    slidesToShow={Math.min(3, mockupImages.length)}
                                    swipeToSlide={true}
                                    draggable={true}
                                    infinite={true}
                                    nextArrow={<ChevronRight />}
                                    prevArrow={<ChevronLeft />}
                                >
                                    {mockupImages.map((url, index) => (
                                        <div key={index}>
                                            <div
                                                className={classNames('SwitchMockupViewButton', {active: selectedMockupView === index})}
                                                onClick={() => setSelectedMockupView(index)}
                                            >
                                                <img src={url} alt={index} className="w-100 h-100" />
                                            </div>
                                        </div>
                                    ))}
                                </Slider>
                            </div>
                        </div>
                    </div>
                )}
                <div className="position-relative w-100 h-100" style={{display: previewMode === '3d' ? 'block' : 'none'}}>
                    <canvas ref={preview3DCanvasRef} className="position-absolute w-100 h-100" />
                </div>
                <div
                    id="pixi-canvas"
                    className="position-relative w-100 h-100"
                    style={{display: previewMode === 'pixi' ? 'flex' : 'none'}}
                >
                    {(!isReady || isUpdating) && (
                        <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" style={{zIndex: 10, background: 'rgba(255,255,255,0.3)'}}>
                            <div className="spinner-border text-secondary"></div>
                        </div>
                    )}
                    <canvas ref={pixiCanvasRef} className="position-absolute w-100 h-100" />
                </div>
            </div>
            {!isCustomShape && (
                <p className={'Notice'}>
                    <span className={'text-danger'}>*</span> Notice: 3D mockup view for size L
                </p>
            )}
        </div>
    )
})

export default PreviewLiveArtworkContainer
